import type { WidgetConfig, ProfessionalSummary, AvailabilityResponse } from '../../types';
import {
  getTodayYMD,
  getWeekDates,
  formatWeekRange,
  storeOpenAvailabilityParamsForRedirect,
  shouldShowPartnerBranding,
} from '../../utils';
import { AvailabilityModal } from '../modal';
import { WeeklyCalendarHTMLBuilder } from './html-builder';

/**
 * Renders the weekly calendar layout
 */
export class WeeklyCalendarLayout {
  private config: WidgetConfig;
  private container: HTMLElement;
  private weekOffset: number = 0;
  private prosByDate: Record<string, ProfessionalSummary[]> = {};
  private todayYMD: string;
  private modal: AvailabilityModal;
  private htmlBuilder: WeeklyCalendarHTMLBuilder;
  /** Tracks whether the one-time container/window listeners have been attached */
  private persistentListenersAttached: boolean = false;

  constructor(container: HTMLElement, config: WidgetConfig) {
    this.container = container;
    // Store reference to config object (not a copy) so updates are reflected
    this.config = config;
    this.todayYMD = getTodayYMD();
    this.modal = new AvailabilityModal(container, config);
    this.htmlBuilder = new WeeklyCalendarHTMLBuilder(config, this.prosByDate, this.todayYMD, this.weekOffset);
  }


  /**
   * Renders the complete widget UI
   */
  render(availability: AvailabilityResponse, weekOffset: number = 0): void {
    this.weekOffset = weekOffset;
    const { start, end } = getWeekDates(weekOffset);

    // Store professionals by date
    // Support both response formats: summaries array or count
    this.prosByDate = {};
    Object.entries(availability).forEach(([date, data]) => {
      // Use summaries if available, otherwise create empty array (count-only response)
      const dayData = data as { available_professional_summaries?: ProfessionalSummary[] };
      this.prosByDate[date] = dayData.available_professional_summaries || [];
    });

    // Update modal with new data
    this.modal.updateProsByDate(this.prosByDate);

    // Update HTML builder with latest data and current week offset
    this.htmlBuilder = new WeeklyCalendarHTMLBuilder(this.config, this.prosByDate, this.todayYMD, this.weekOffset);

    // Build HTML
    const html = this.htmlBuilder.buildHTML(start, end);
    this.container.innerHTML = html;

    // Append modal to body for proper z-index stacking (outside widget container)
    this.modal.ensureInBody();

    // Attach event listeners
    this.attachEventListeners();

    // Attach modal event listeners
    this.modal.attachEventListeners();

    // Update tabs mode after DOM is ready and styles are computed
    // Use double requestAnimationFrame to ensure CSS is applied and grid columns are computed
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateTabsMode();
      });
    });

    // Check query params after render (only if we have data for the date)
    requestAnimationFrame(() => {
      this.modal.checkQueryParamsForModal();
    });
  }


  /**
   * Attaches event listeners
   */
  private attachEventListeners(): void {
    const form = this.container.querySelector('#ondiem-filter-form') as HTMLFormElement;
    const zipInput = this.container.querySelector('#ondiem-zip-input') as HTMLInputElement;
    const positionSelect = this.container.querySelector('#ondiem-position-select') as HTMLSelectElement;
    const prevBtn = this.container.querySelector('#prev-week-btn') as HTMLElement;
    const nextBtn = this.container.querySelector('#next-week-btn') as HTMLElement;

    if (form) {
      form.addEventListener('submit', (e) => e.preventDefault());
    }

    if (zipInput) {
      const zipError = this.container.querySelector('#ondiem-zip-error') as HTMLElement | null;

      const setZipError = (show: boolean): void => {
        zipInput.classList.toggle('is-error', show);
        zipError?.classList.toggle('is-visible', show);
      };

      // Only fire when the zip is a complete 5-digit value or empty — never on partial input.
      // This avoids the need for a debounce timeout and eliminates double-calls with partial zips.
      zipInput.addEventListener('input', () => {
        const zip = zipInput.value.trim();
        const isEmpty = zip === '';
        const isValidZip = /^\d{5}$/.test(zip);
        // Only clear the error while typing — never show it mid-input.
        if (isEmpty || isValidZip) {
          setZipError(false);
          this.onFilterChange();
          // Validate that the zip actually exists; shows error after lookup if not real.
          if (isValidZip) void this.validateZipExists(zip, setZipError);
        }
      });
      // Show format error on blur; also check existence for complete-but-fake zips.
      zipInput.addEventListener('blur', () => {
        const zip = zipInput.value.trim();
        if (zip !== '' && !/^\d{5}$/.test(zip)) {
          setZipError(true);
        } else if (/^\d{5}$/.test(zip)) {
          void this.validateZipExists(zip, setZipError);
        }
      });
      zipInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const zip = zipInput.value.trim();
          const isValid = zip === '' || /^\d{5}$/.test(zip);
          setZipError(!isValid);
          if (isValid) {
            this.onFilterChange();
            if (zip) void this.validateZipExists(zip, setZipError);
          }
        }
      });
    }

    if (positionSelect) {
      positionSelect.addEventListener('change', () => this.onFilterChange());
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.onPrevWeek());
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.onNextWeek());
    }

    // These listeners are on persistent elements (window, container) and must only be
    // added once — not on every render, or they accumulate unboundedly.
    if (!this.persistentListenersAttached) {
      this.persistentListenersAttached = true;

      // Update tabs mode on window resize (breakpoint changes)
      let resizeTimeout: number;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
          this.updateTabsMode();
        }, 150);
      });

      // Show more: on ondiem.com open modal in-place; on 3rd party sites store params and redirect
      this.container.addEventListener('click', (e) => {
        const link = (e.target as HTMLElement).closest('.pro-availability_show-more-btn');
        if (!link || !(link instanceof HTMLAnchorElement)) return;
        if (link.getAttribute('data-open-modal') === '1') {
          const date = link.getAttribute('data-date');
          if (!date) return;
          e.preventDefault();
          this.modal.open(date);
          return;
        }
        // Redirect link: store params in sessionStorage so destination page can open modal
        // even if it strips the URL (e.g. Webflow) before our script runs
        const href = link.getAttribute('href');
        if (href && href !== '#' && !href.startsWith('#')) {
          storeOpenAvailabilityParamsForRedirect(href);
        }
      });
    }
  }

  /**
   * Handles filter changes
   */
  private onFilterChange(): void {
    const zipInput = this.container.querySelector('#ondiem-zip-input') as HTMLInputElement;
    const positionSelect = this.container.querySelector('#ondiem-position-select') as HTMLSelectElement;

    const zip = (zipInput?.value || '').trim();
    const position = (positionSelect?.value || '').trim();

    // Update config
    this.config.zipCode = zip || null;
    this.config.position = position || null;

    // Trigger re-fetch (will be handled by widget)
    this.container.dispatchEvent(
      new CustomEvent('ondiem:filter-change', {
        detail: { zip, position },
      })
    );
  }

  /**
   * Handles previous week navigation
   */
  private onPrevWeek(): void {
    if (this.weekOffset > 0) {
      this.container.dispatchEvent(
        new CustomEvent('ondiem:week-change', {
          detail: { direction: 'prev', offset: this.weekOffset - 1 },
        })
      );
    }
  }

  /**
   * Handles next week navigation
   */
  private onNextWeek(): void {
    this.container.dispatchEvent(
      new CustomEvent('ondiem:week-change', {
        detail: { direction: 'next', offset: this.weekOffset + 1 },
      })
    );
  }

  /**
   * Updates week offset and re-renders navigation
   */
  updateWeekOffset(offset: number): void {
    this.weekOffset = offset;
    const { start, end } = getWeekDates(offset);
    const weekRange = this.container.querySelector('#week-range-label');
    const prevBtn = this.container.querySelector('#prev-week-btn') as HTMLElement;

    if (weekRange) {
      weekRange.textContent = formatWeekRange(start, end);
    }

    if (prevBtn) {
      // Partner variant keeps the chevron in place and mutes it via .is-disabled;
      // the standard variant hides it outright. Mirrors buildWeekNavigation().
      const isPartner = shouldShowPartnerBranding(this.config.branding);
      prevBtn.classList.toggle('is-disabled', offset === 0);
      prevBtn.style.visibility = offset === 0 && !isPartner ? 'hidden' : 'visible';
    }

    // Update tabs mode when week offset changes (past days may have changed)
    requestAnimationFrame(() => {
      this.updateTabsMode();
    });
  }

  /**
   * Updates config and rebuilds HTML builder
   */
  updateConfig(config: WidgetConfig): void {
    this.config = config;
    this.modal.updateConfig(config);
    // Rebuild HTML builder with new config and current week offset
    this.htmlBuilder = new WeeklyCalendarHTMLBuilder(this.config, this.prosByDate, this.todayYMD, this.weekOffset);
  }

  /**
   * Validates that a 5-digit zip actually exists via the zippopotam.us lookup.
   * Called after format validation passes to catch fake-but-valid-format zips
   * (e.g. "11111") that the availability API may accept without returning an error.
   * Shows the error only if the input still holds the same zip when the async
   * lookup completes — prevents stale results from clobbering a newer value.
   */
  private async validateZipExists(
    zip: string,
    setZipError: (show: boolean) => void
  ): Promise<void> {
    try {
      const { lookupCityState } = await import('../../api');
      const result = await lookupCityState(zip);
      const currentZip = (this.container.querySelector('#ondiem-zip-input') as HTMLInputElement | null)?.value?.trim();
      if (currentZip === zip) {
        setZipError(result === null);
      }
    } catch {
      // Lookup failure is non-fatal; leave error state as-is.
    }
  }

  /**
   * Shows or clears the zip input error state. Called externally when the API
   * rejects the zip code (e.g. zip_code_not_found), after render() has run.
   */
  setZipError(show: boolean): void {
    const zipInput = this.container.querySelector('#ondiem-zip-input') as HTMLInputElement | null;
    const zipError = this.container.querySelector('#ondiem-zip-error') as HTMLElement | null;
    zipInput?.classList.toggle('is-error', show);
    zipError?.classList.toggle('is-visible', show);
  }

  /**
   * Shows/hides loader
   */
  setLoading(loading: boolean): void {
    const loader = this.container.querySelector('#calendar-loader');
    const calendar = this.container.querySelector('#days-container');

    if (loader) {
      loader.classList.toggle('hidden', !loading);
    }

    if (calendar) {
      calendar.classList.toggle('hidden', loading);
    }
  }

  /**
   * Determines if tabs mode should be enabled and updates the UI accordingly
   * Tabs mode is enabled when:
   * - There are past days (hasPast)
   * - The number of days (7) exceeds the visible grid columns (needsTabs)
   */
  private updateTabsMode(): void {
    const host = this.container.querySelector('.pro-availability_host') as HTMLElement;
    const tabsContainer = this.container.querySelector('.pro-availability_tabs') as HTMLElement;
    const daysContainer = this.container.querySelector('#days-container') as HTMLElement;

    if (!host || !tabsContainer || !daysContainer) {
      return;
    }

    // Check if there are past days
    const pastDayWrappers = this.container.querySelectorAll('[data-date].is-past');
    const hasPast = pastDayWrappers.length > 0;

    // Get the number of visible grid columns from computed style
    const computedStyle = window.getComputedStyle(daysContainer);
    const gridTemplateColumns = computedStyle.gridTemplateColumns;
    
    // Parse grid-template-columns to count columns
    // e.g., "repeat(7, minmax(0px, 1fr))" or "200px 200px 200px" etc.
    let trackCount = 0;
    if (gridTemplateColumns.includes('repeat')) {
      // Extract number from repeat() syntax
      const match = gridTemplateColumns.match(/repeat\((\d+)/);
      if (match) {
        trackCount = parseInt(match[1], 10);
      }
    } else {
      // Count space-separated values
      trackCount = gridTemplateColumns.split(' ').filter(v => v.trim()).length;
    }

    // Tabs are needed if we have 7 days but fewer visible columns
    const dayCount = 7;
    const needsTabs = dayCount > trackCount;
    const shouldTabs = needsTabs && hasPast;

    if (shouldTabs) {
      // Enable tabs mode
      host.classList.add('is-tabs');
      
      // Create and add tab elements for past days
      this.createTabsForPastDays(tabsContainer, pastDayWrappers);
    } else {
      // Disable tabs mode
      host.classList.remove('is-tabs');
      
      // Clear tabs container
      tabsContainer.innerHTML = '';
    }
  }

  /**
   * Creates tab elements for past days and adds them to the tabs container
   */
  private createTabsForPastDays(
    tabsContainer: HTMLElement,
    pastDayWrappers: NodeListOf<Element>
  ): void {
    // Clear existing tabs
    tabsContainer.innerHTML = '';

    pastDayWrappers.forEach((dayWrapper) => {
      const date = dayWrapper.getAttribute('data-date');
      if (!date) return;

      // Get the day card heading to extract weekday and date label
      const heading = dayWrapper.querySelector('.pro-availability_day-card-heading');
      if (!heading) return;

      const weekdayEl = heading.querySelector('.pro-availability_weekday-value');
      const dateLabelEl = heading.querySelector('.pro-availability_date-label');

      // Extract text - weekday includes " - " separator, dateLabel is just the date
      const weekday = weekdayEl?.textContent?.trim() || '';
      const dateLabel = dateLabelEl?.textContent?.trim() || '';

      // Format: "Sunday - 1/25" (weekday already includes " - ")
      const tabText = `${weekday}${dateLabel}`;

      // Create tab element
      const tab = document.createElement('div');
      tab.className = 'pro-availability_tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('data-date', date);
      tab.setAttribute('aria-label', `View ${tabText}`);
      tab.textContent = tabText;

      tabsContainer.appendChild(tab);
    });
  }

}
